// @file Helpers for Pragmatic DnD.
import { asciiSafeStringify } from '@@/bits/json_stringify'
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge'
import type { ExternalDragPayload, NativeMediaType } from '@atlaskit/pragmatic-drag-and-drop/dist/types/internal-types'
import {
  type ElementDragPayload,
  type ElementDropTargetEventBasePayload,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { type ElementDropTargetEventBasePayload as ExternalDropTargetEventBasePayload } from '@atlaskit/pragmatic-drag-and-drop/external/adapter'
import type { DropTargetRecord } from '@atlaskit/pragmatic-drag-and-drop/types'
import type { Ref } from 'vue'

type LogicalEdge = 'top' | 'end' | 'bottom' | 'start'

/** Symbol to identify events made by Pragmatic DnD. */
const ITEM_KEY = Symbol('item')

type DragData = Record<string | symbol, unknown>

interface DragDataForExternal {
  /**
   * String representation of the drag source.
   */
  text?: string
  /**
   * HTML representation of the drag source.
   */
  html?: string
  /**
   * Data to be serialized under the custom drag type.
   * @example "application/vnd.padlet-${type}+json": { ...<serialized data>... }
   */
  dragData?: DragData
}

interface ItemData<TData = DragData> {
  [K: symbol | string]: unknown
  type: string
  data: TData
  [ITEM_KEY]: true
}

interface DraggableSource {
  /**
   * Type of draggable elements that can be dropped on this target.
   */
  type: ItemData['type']
  /**
   * Which axis the draggable elements of this type move on.
   */
  axis: 'vertical' | 'horizontal'
  /**
   * Optional reactive value for axis. If provided, the value will be watched for changes.
   */
  axisRef?: Ref<'vertical' | 'horizontal'>
}

type RelativePosition = 'before' | 'after'

interface CanDropPayload<TSourceData, TTargetData> {
  sourceItem: ItemData<TSourceData>
  targetItem: ItemData<TTargetData>
}

interface OnDropPayload<TSourceData, TTargetData> {
  sourceItem: ItemData<TSourceData>
  targetItem: ItemData<TTargetData>
  relativePositionToTarget: RelativePosition
}

const makeItemData = (data: DragData, type: ItemData['type']): ItemData => {
  return { data, type, [ITEM_KEY]: true }
}

const extractItemData = (payload: ElementDragPayload | DropTargetRecord): ItemData | null => {
  const sourceType = payload.data.type
  const data = payload.data.data
  if (sourceType == null || data == null) return null
  return { ...payload.data, [ITEM_KEY]: true } as unknown as ItemData
}

/**
 * Returns whether the given object is a drag item object made by us,
 * which would have the `ITEM_KEY` symbol.
 */
const isItemData = (data: any): data is ItemData => {
  return data[ITEM_KEY] === true
}

/**
 * Creates a custom media type for the given item type.
 * @see https://en.wikipedia.org/wiki/Media_type
 */
const createCustomMediaType = (type: ItemData['type']): string => {
  return `application/vnd.padlet-${type}+json` // We store data in JSON format.
}

const extractCustomMediaType = (payload: ExternalDragPayload): string | undefined => {
  return payload.types
    .find((type) => type.startsWith('application/vnd.padlet-') && type.endsWith('+json'))
    ?.replace('application/vnd.padlet-', '')
    .replace('+json', '')
}

/**
 * Composes the data to be set on the `DataTransfer` object.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/HTML_Drag_and_Drop_API#define_the_drags_data
 */
const makeItemDataForExternal = (
  data: DragDataForExternal,
  type: ItemData['type'],
): { [K in NativeMediaType]?: string } => {
  return {
    'text/plain': data.text,
    'text/html': data.html,
    // Serialize custom data in JSON format.
    [createCustomMediaType(type)]: data.dragData != null ? asciiSafeStringify(data.dragData) : undefined,
  }
}

const extractItemDataFromExternal = (payload: ExternalDragPayload): ItemData | null => {
  const sourceType = extractCustomMediaType(payload)
  if (sourceType == null) return null
  const rawData = payload.getStringData(createCustomMediaType(sourceType))
  if (rawData == null) return null
  return makeItemData(JSON.parse(rawData), sourceType)
}

const extractLogicalClosestEdge = (target: DropTargetRecord): LogicalEdge | null => {
  const { element, data } = target
  const isRtl = element.closest('[dir="rtl"]') != null
  const closestEdge = extractClosestEdge(data)
  if (closestEdge == null) return null
  if (closestEdge === 'top' || closestEdge === 'bottom') return closestEdge
  if (closestEdge === 'left') return isRtl ? 'end' : 'start'
  return isRtl ? 'start' : 'end'
}

/**
 * Extracts the closest edge value added by `attachClosestEdge` and converts it
 * to non-directional relative position.
 */
const extractRelativePositionToTarget = (target: DropTargetRecord): RelativePosition | null => {
  const closestEdge = extractLogicalClosestEdge(target)
  if (closestEdge == null) return null
  if (closestEdge === 'top' || closestEdge === 'start') return 'before'
  return 'after'
}

const isInnermostDropTarget = (e: ElementDropTargetEventBasePayload | ExternalDropTargetEventBasePayload): boolean => {
  // `dropTargets` is sorted by event bubbling order, so the innermost target is the first one.
  const innermostTarget = e.location.current.dropTargets[0]
  return innermostTarget != null && innermostTarget.element === e.self.element
}

export {
  extractCustomMediaType,
  extractItemData,
  extractItemDataFromExternal,
  extractLogicalClosestEdge,
  extractRelativePositionToTarget,
  isInnermostDropTarget,
  isItemData,
  ITEM_KEY,
  makeItemData,
  makeItemDataForExternal,
}
export type {
  CanDropPayload,
  DragData,
  DragDataForExternal,
  DraggableSource,
  ItemData,
  LogicalEdge,
  OnDropPayload,
  RelativePosition,
}
